import { isEmpty } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { LinkModal } from '../../../../components/ATS/LinkModal/LinkModal';
import { useDontShowAgain } from '../../../../components/ATS/LinkModal/use-dont-show-again';
import { Thumbnail } from '../../../../components/thumbnail/thumbnail';
import { ScriptService } from '../../../../controllers/ScriptService';
import i18n from '../../../../i18n';
import { SvgCart, SvgSchedule } from '../../../../icons';
import { EpisodeAsset } from '../../../../queries/ats/titles/types/asset';
import { ThirdPartySource } from '../../../../queries/ats/titles/types/sources';
import { getAltiboxServiceUrl } from '../../../../utils/svodUtil';
import { useDetailsImage } from '../../use-details-image';
import { EpisodeThumbnailExternalLink } from './episode-thumbnail-external-link';

interface ThirdpartyThumbnailProps {
  episode: EpisodeAsset;
  source: ThirdPartySource;
}

export function EpisodeThirdPartyThumbnail({ episode, source }: ThirdpartyThumbnailProps) {
  const { t } = useTranslation();
  const linkHref = source.links.play || source.links.open;
  const secondaryButtonLabel = t(ScriptService.isDKUser() ? 'order' : 'go to mine sider');
  const { isChecked: modalIsDisabledByUser } = useDontShowAgain(source.id);

  const shouldProceedToDeeplink = modalIsDisabledByUser || source.isSubscribed;

  const src = useDetailsImage({ asset: episode, shape: 'wide', width: 200 });

  const image = {
    src: src,
    alt: `${episode.titleName} cover`,
  };

  if (!linkHref || isEmpty(linkHref)) {
    throw new Error(`Link url are missing for ${source.name} item in thumbnail.`);
  }

  if (moment().isBefore(source.availableFrom)) {
    return <FutureThirdPartyEpisodeThumbnail episode={episode} source={source} />;
  }

  if (shouldProceedToDeeplink) {
    return <EpisodeThumbnailExternalLink source={source} image={image} url={linkHref} />;
  }

  return (
    <LinkModal
      id={source.id}
      title={`${t('before opening')} ${source.name}`}
      description={modalDescriptionText(source)}
      primaryButton={{
        title: `${t('continue to')} ${source.name}`,
        url: linkHref,
      }}
      secondaryButton={{
        title: secondaryButtonLabel,
        url: getAltiboxServiceUrl(source.providerIdentifier),
      }}
    >
      <Thumbnail image={image} icon={SvgCart} iconText={t('get access')} />
    </LinkModal>
  );
}

function modalDescriptionText(source: ThirdPartySource) {
  switch (source.id) {
    case 'Viaplay':
    case 'HBO_Max':
      return i18n.t(`pop-up message hbo-max and viaplay${ScriptService.isDKUser() ? ' dk users' : ''}`, {
        service: source.name,
      });
    case 'prime_video':
      return i18n.t('pop-up message prime-video');
    default:
      return i18n.t('order a subscription');
  }
}

interface FutureThirdPartyEpisodeThumbnailProps {
  episode: EpisodeAsset;
  source: ThirdPartySource;
}

function FutureThirdPartyEpisodeThumbnail({ episode, source }: FutureThirdPartyEpisodeThumbnailProps) {
  const { t } = useTranslation();
  const url = source.links.open || source.links.play;

  const secondaryButtonLabel = t(ScriptService.isDKUser() ? 'order' : 'go to mine sider');
  const { isChecked } = useDontShowAgain(source.id);

  const shouldProceedToDeeplink = isChecked || source.isSubscribed;

  const src = useDetailsImage({ asset: episode, shape: 'wide', width: 200 });

  const image = {
    src: src,
    alt: `${episode.titleName} cover`,
  };

  if (!url || isEmpty(url)) {
    throw new Error(`Link url are missing for ${source.name} item in thumbnail.`);
  }

  if (shouldProceedToDeeplink) {
    return (
      <Thumbnail
        image={image}
        icon={SvgSchedule}
        iconText={source.isSubscribed ? undefined : t('get access')}
        interactions={{
          backdrop: 'always',
          icon: 'always',
        }}
      />
    );
  }

  return (
    <LinkModal
      id={source.id}
      title={`${t('before opening')} ${source.name}`}
      description={modalDescriptionText(source)}
      primaryButton={{
        title: `${t('continue to')} ${source.name}`,
        url: url,
      }}
      secondaryButton={{
        title: secondaryButtonLabel,
        url: getAltiboxServiceUrl(source.providerIdentifier),
      }}
    >
      <Thumbnail
        image={image}
        icon={SvgSchedule}
        iconText={source.isSubscribed ? undefined : t('get access')}
        interactions={{
          backdrop: 'always',
          icon: 'always',
        }}
      />
    </LinkModal>
  );
}
